import './registerServiceWorker';
import './styles/main.scss';

import { abilitiesPlugin } from '@casl/vue';
import { createApp } from 'vue';
import VueGtag from 'vue-gtag-next';

import { aclPlugin, appAbility } from '@/common/abilities';
import { loadingStatePlugin } from '@/common/loading';
import { dialogPlugin, toastPlugin } from '@/common/popup-systems';
import * as baseComponents from '@/components/base';
import * as contentPlaceholders from '@/components/placeholders';

import App from './App.vue';
import { globalComponentsPlugin, preloaderPlugin } from './plugins';
import { router } from './router';
import store from './store';

// Suppress ResizeObserver errors - this is a known browser quirk that doesn't affect functionality
const resizeObserverErrorHandler = (e: ErrorEvent) => {
	const errorMessage = e.message || e.error?.message || String(e.error || '');
	if (
		errorMessage.includes('ResizeObserver loop completed with undelivered notifications') ||
		errorMessage.includes('ResizeObserver loop limit exceeded') ||
		errorMessage.includes('ResizeObserver')
	) {
		e.stopImmediatePropagation();
		e.preventDefault();
		return false;
	}
	return true;
};

// Override console.error to catch ResizeObserver errors
const originalConsoleError = console.error;
console.error = (...args: any[]) => {
	// Safely check for ResizeObserver messages without causing recursion
	try {
		// Only check first few string arguments to avoid recursion with proxy objects
		const stringArgs = args.slice(0, 3).filter(arg => typeof arg === 'string');
		const message = stringArgs.join(' ');
		if (
			message.includes('ResizeObserver loop completed with undelivered notifications') ||
			message.includes('ResizeObserver loop limit exceeded') ||
			message.includes('ResizeObserver')
		) {
			return; // Suppress ResizeObserver errors
		}
	} catch (e) {
		// If checking fails, just pass through to avoid recursion
	}
	originalConsoleError.apply(console, args);
};

window.addEventListener('error', resizeObserverErrorHandler, true);

const app = createApp(App);

app.config.unwrapInjectedRef = true;

// Also handle unhandled promise rejections for ResizeObserver
window.addEventListener('unhandledrejection', e => {
	const reason = e.reason?.message || String(e.reason || '');
	if (reason.includes('ResizeObserver') || reason.includes('ResizeObserver loop')) {
		e.preventDefault();
	}
});

// Suppress ResizeObserver warnings in console
const originalWarn = console.warn;
console.warn = (...args: any[]) => {
	// Safely check for ResizeObserver messages without causing recursion
	try {
		// Only check first few string arguments to avoid recursion with proxy objects
		const stringArgs = args.slice(0, 3).filter(arg => typeof arg === 'string');
		const message = stringArgs.join(' ');
		if (message.includes('ResizeObserver')) {
			return; // Suppress ResizeObserver warnings
		}
	} catch (e) {
		// If checking fails, just pass through to avoid recursion
	}
	originalWarn.apply(console, args);
};

app
	.use(store)
	.use(router)
	.use(loadingStatePlugin)
	.use(globalComponentsPlugin([baseComponents, contentPlaceholders]))
	.use(dialogPlugin)
	.use(preloaderPlugin)
	.use(toastPlugin)
	.use(abilitiesPlugin, appAbility, { useGlobalProperties: true })
	.use(aclPlugin)
	.use(VueGtag, { property: { id: 'UA-103249441-1' } })
	.mount('#app');
